// PRODUCT-26110 Phase 2B2 Task 1 (floating-vue): floating-vue is Vue-3-native --
// its `install(app, opts)` (aliased here as `app.use(FloatingVue, opts)`) calls real
// Vue 3 App APIs (`app.directive()`, `app.component()`, a reactive popper-state
// context, etc.), the same category of call that `@vue/compat`'s global `Vue.use()`
// singleton shim cannot satisfy (see quasar.ts's identical Quasar 2 precedent, and
// Application.ts's now-removed `this.options.Vue.use(createTooltipPlugin() as any)`
// call). So, like Quasar 2, this exports a plain installer function instead of
// calling `.use()` at module-evaluation time; the caller (tprm-portal's main.ts)
// threads it through `appPlugins` (see types/Application.ts) so it runs against the
// real `createApp()` instance in Application.ts's `mount()`.
//
// floating-vue's base CSS (popper positioning/transition mechanics + built-in theme
// defaults) is imported here, in the same file that exports the installer -- mirrors
// quasar.ts's own `import './quasar.scss'` placement. `./index.scss` (below) carries
// this project's theme overrides on top of that base CSS; see that file's header
// comment and floating-vue-findings.md's Task 1 theme-mapping table for the full
// old-class -> new-class mapping.
import type { App } from 'vue';

import FloatingVue from 'floating-vue';
import 'floating-vue/dist/style.css';
import './index.scss';

// Old v-tooltip config (for reference, see git history) exposed two distinct looks:
//   - the plain `v-tooltip` directive default (dark, compact) -- floating-vue's
//     built-in `tooltip` theme is the direct successor (same directive name,
//     same author/library lineage), overridden below to match the old look.
//   - the `<v-popover>` component / `.popover` variant (light, padded, shadowed)
//     -- floating-vue's built-in `dropdown` theme (paired with the `VDropdown`
//     component) is the direct successor, overridden below to match.
// Built-in theme names can be overridden in place by reusing them as keys here
// (confirmed via floating-vue's docs) -- no need to invent new theme names for
// parity; component/directive usage files (Task 2/3) will reference `tooltip`/
// `dropdown` by name (or rely on the defaults, since `VTooltip`/`VDropdown` already
// default to them).
export function installFloatingVue(app: App): void {
    app.use(FloatingVue, {
        themes: {
            tooltip: {
                // Matches old v-tooltip's `defaultTemplate`/`defaultClass` look --
                // see index.scss's `.v-popper--theme-tooltip` rules for the actual
                // background/padding/border-radius/z-index values.
            },
            dropdown: {
                // Matches old v-tooltip's `popover.defaultClass` ('VTooltip--popover')
                // look -- see index.scss's `.v-popper--theme-dropdown` rules.
            },
        },
    });
}

export default installFloatingVue;
